import { useRef, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/assetsLiabilities";
import toast from "react-hot-toast";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { useAddAssetsLiabilitiesImages } from "app/hooks/assetsLiabilities/useAddAssetsLiabilitiesImages";
import pdf_icon from "app/assets/icons/pdf_icon.svg";
import UploadIcon from "app/assets/icons/UploadIcon";
import DeleteIcon from "app/assets/icons/DeleteIcon";
import Loader from "app/components/common/Loader";
import { usePresignedUrls } from "app/hooks/assetsLiabilities/usePresignedUrls";
import { useS3Upload } from "app/hooks/assetsLiabilities/useS3Upload";
import { handleApiError } from "app/utils/handleApiError";
import { createFileChangeHandler } from "app/utils/fileChangeHandler";

interface FormData {
  frontImage: File | null;
  backImage: File | null;
}
interface Props {
  hasValidPassport: boolean;
}
const UploadCertificateMedicareCard: React.FC<Props> = ({
  hasValidPassport,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: getPresignedUrls } = usePresignedUrls();
  const { mutate: uploadS3 } = useS3Upload();
  const [frontPreview, setfrontPreview] = useState<string | null>(null);
  const [backPreview, setbackPreview] = useState<string | null>(null);
  const { mutate: uploadImages, isPending } = useAddAssetsLiabilitiesImages();
  const { isValidPassportImages: isValidPassport, ...others } = useSelector(
    (state: RootState) => state.assetsLiabilities.stepsData
  );
  const { applicantID, ...savedIds } = useSelector(
    (state: RootState) => state.loanTypesSteps?.savedIds
  );
  const frontFileInputRef = useRef<HTMLInputElement>(null);
  const backFileInputRef = useRef<HTMLInputElement>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    setError,
    trigger,
  } = useForm<FormData>();
  const frontImage = watch("frontImage");
  const backImage = watch("backImage");

  const onSubmit = async (data: FormData) => {
    if (!applicantID) {
      toast.error("Applicant id is required");
      return;
    }
    const { backImage, frontImage } = data;
    try {
      setIsLoading(true);
      if (!(frontImage instanceof File) && !(backImage instanceof File)) {
        dispatch(nextStep());
        return;
      }

      const documentTypes: string[] = [];
      const metadata: Record<string, any> = {};

      const uploadData: Record<string, File> = {};
      if (frontImage instanceof File) {
        documentTypes.push("birth_or_citizenship_certificate");
        uploadData.certificate = frontImage;
      }
      if (backImage instanceof File) {
        documentTypes.push("medicare_card");
        uploadData.medicareCard = backImage;
      }
      if (!documentTypes.length) {
        dispatch(nextStep());
        return;
      }
      getPresignedUrls(
        {
          url: `/document/${applicantID}/presigned-urls`,
          data: { documentTypes },
        },
        {
          onSuccess: async (presignedData) => {
            try {
              if (frontImage instanceof File) {
                const { url, key } =
                  presignedData.birth_or_citizenship_certificate || {};
                await uploadToS3(url, frontImage);
                metadata["certificate"] = {
                  key,
                  fileName: frontImage.name,
                  mimeType: frontImage.type,
                };
              }

              if (backImage instanceof File) {
                const { url, key } = presignedData.medicare_card || {};
                await uploadToS3(url, backImage);
                metadata["medicareCard"] = {
                  key,
                  fileName: backImage.name,
                  mimeType: backImage.type,
                };
              }

              uploadImages(
                {
                  url: `/document/${applicantID}/identity-docs`,
                  data: metadata,
                },
                {
                  onSuccess: (response: any) => {
                    const images: { [key: string]: string } = {};
                    response.forEach((item: any) => {
                      if (item.type === "birth_or_citizenship_certificate") {
                        images.frontImage = item.url;
                      } else if (item.type === "medicare_card") {
                        images.backImage = item.url;
                      }
                    });
                    dispatch(
                      addStepsData({
                        ...others,
                        IsvalidPassport: hasValidPassport,
                        isValidPassportImages: {
                          backImage: images.backImage,
                          frontImage: images.frontImage,
                        },
                      })
                    );
                    dispatch(nextStep());
                    setIsLoading(false);
                  },
                  onError: (err) => {
                    setIsLoading(false);
                    toast.error("Saving metadata failed.");
                  },
                }
              );
            } catch (err) {
              toast.error("S3 upload failed.");
            }
          },
          onError: () => {
            setIsLoading(false);
            toast.error("Failed to get presigned URLs.");
          },
        }
      );
    } catch (error: any) {
      setIsLoading(false);
      handleApiError(error);
    }
  };

  const uploadToS3 = (url: string, file: File) => {
    return new Promise<void>((resolve, reject) => {
      uploadS3(
        { url, data: file },
        {
          onSuccess: (status: any) => (status === 200 ? resolve() : reject()),
          onError: reject,
        }
      );
    });
  };

  const handleFileChooseClick = (type: "front" | "back") => {
    if (type === "front" && frontFileInputRef.current) {
      frontFileInputRef.current.click();
    } else if (type === "back" && backFileInputRef.current) {
      backFileInputRef.current.click();
    }
  };

  const handleFrontFileChange = createFileChangeHandler<"frontImage">({
    fieldName: "frontImage",
    setValue,
    setError,
    clearErrors,
    trigger,
    setPreview: setfrontPreview,
  });
  const handleBackFileChange = createFileChangeHandler<"backImage">({
    fieldName: "backImage",
    setValue,
    setError,
    clearErrors,
    trigger,
    setPreview: setbackPreview,
  });
  // const handleFileChange = (
  //   e: React.ChangeEvent<HTMLInputElement>,
  //   fileType: "front" | "back"
  // ) => {
  //   const file = e.target.files?.[0] ?? null;
  //   const fieldName = fileType === "front" ? "frontImage" : "backImage";
  //   clearErrors(fieldName);
  //   e.target.value = "";

  //   if (!file) return;
  //   if (file.size > 5 * 1024 * 1024) {
  //     setError(fieldName, {
  //       type: "fileSize",
  //       message: "File size should not exceed 5MB",
  //     });
  //     return;
  //   }

  //   const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
  //   if (!allowedTypes.includes(file.type)) {
  //     setError(fieldName, {
  //       type: "fileType",
  //       message: "Invalid file type. Only JPG, PNG, or PDF allowed",
  //     });
  //     return;
  //   }
  //   setValue(fieldName, file);
  //   clearErrors(fieldName);
  // };

  useEffect(() => {
    if (frontImage instanceof File) {
      const objectUrl = URL.createObjectURL(frontImage);
      setfrontPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (isValidPassport?.frontImage) {
      setfrontPreview(isValidPassport?.frontImage);
    }
  }, [frontImage, isValidPassport?.frontImage]);

  useEffect(() => {
    if (backImage instanceof File) {
      const objectUrl = URL.createObjectURL(backImage);
      setbackPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (isValidPassport?.backImage) {
      setbackPreview(isValidPassport?.backImage);
    }
  }, [backImage, isValidPassport?.backImage]);

  const handleDeleteImage = (type: "frontImage" | "backImage") => {
    if (type === "frontImage") {
      dispatch(
        addStepsData({
          ...others,
          isValidPassportImages: {
            ...isValidPassport,
            frontImage: "",
          },
        })
      );
      setValue("frontImage", null);
      setfrontPreview(null);
    } else if (type === "backImage") {
      setValue("backImage", null);
      dispatch(
        addStepsData({
          ...others,
          isValidPassportImages: {
            ...isValidPassport,
            backImage: "",
          },
        })
      );
      setbackPreview(null);
    }
  };
  return (
    <>
      {isPending && (
        <Loader text="Please wait while your documents are being uploaded..." />
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className="text-left">
          <div className="flex gap-7 sm:gap-6  flex-col lg:flex-row md:flex-col  w-full ">
            <div className="w-full lg:w-[49%] md:w-full">
              {frontPreview ? (
                <div className="bg-[#EAECF0] p-5 w-full text-center rounded-[20px] relative h-[250px] flex justify-center items-center">
                  {frontImage && frontImage.type === "application/pdf" ? (
                    <div className="text-center w-[80%] overflow-hidden mx-auto">
                      <img
                        src={pdf_icon}
                        alt="pdf_icon "
                        className="rounded-[20px] text-center m-auto"
                      />
                      <p className="font-Mulish text-[#000] font-normal text-base w-[80%] overflow-hidden mx-auto">
                        {frontImage.name.length > 25
                          ? `${frontImage.name.slice(0, 25)}...`
                          : frontImage.name}
                      </p>
                    </div>
                  ) : (
                    <img
                      src={frontPreview}
                      alt="Uploaded Preview"
                      className="mx-auto  h-[220px]  rounded-[20px]"
                      onError={(e) => (e.currentTarget.src = pdf_icon)}
                    />
                  )}
                  <DeleteIcon
                    onClick={() => handleDeleteImage("frontImage")}
                    className="absolute cursor-pointer top-[10px] right-[10px]"
                  />
                </div>
              ) : (
                <div className="bg-[#EAECF0] relative py-5 w-full text-center rounded-[20px] h-[250px]">
                  <div className="text-center">
                    <UploadIcon />
                  </div>
                  <h6 className="text-[14px] font-Mulish font-normal w-[90%] sm:w-[80%] mx-auto text-darkgrey mb-3 mt-4">
                    Citizenship certificate or Birth Certificate
                  </h6>
                  <p className="text-[12px] font-normal text-[#98A2B3] w-[90%] sm:w-[80%] mx-auto mb-5">
                    Upload the front side of the document supports: JPG, PNG,
                    PDF
                  </p>
                  <div className="m-auto">
                    <input
                      type="file"
                      accept="image/jpeg, image/png , application/pdf"
                      {...register("frontImage", {
                        required: !isValidPassport?.frontImage
                          ? "File is required"
                          : false,
                      })}
                      // onChange={(e) => handleFileChange(e, "front")}
                      onChange={handleFrontFileChange}
                      ref={frontFileInputRef}
                      hidden
                    />
                    <button
                      type="button"
                      onClick={() => handleFileChooseClick("front")}
                      className="border border-solid border-[#ccc] text-sm font-normal  text-[#585C7B]  rounded-full px-4 py-2"
                    >
                      Choose file
                    </button>
                  </div>
                  {errors.frontImage && (
                    <ErrorsMessage
                      title={errors.frontImage.message}
                      className="absolute top-[100%] left-0"
                    />
                  )}
                </div>
              )}
            </div>
            <div className="w-full lg:w-[49%] md:w-full">
              {backPreview ? (
                <div className="bg-[#EAECF0] p-5 w-full text-center rounded-[20px] relative h-[250px] flex justify-center items-center">
                  {backImage && backImage.type === "application/pdf" ? (
                    <div className="text-center w-[80%] overflow-hidden mx-auto">
                      <img
                        src={pdf_icon}
                        alt="pdf_icon "
                        className="rounded-[20px] text-center m-auto"
                      />
                      <p className="font-Mulish text-[#000] font-normal text-base ">
                        {backImage.name.length > 25
                          ? `${backImage.name.slice(0, 25)}...`
                          : backImage.name}
                      </p>
                    </div>
                  ) : (
                    <img
                      src={backPreview}
                      alt="Uploaded Preview"
                      className="mx-auto  h-[220px]  rounded-[20px]"
                      onError={(e) => (e.currentTarget.src = pdf_icon)}
                    />
                  )}

                  <DeleteIcon
                    onClick={() => handleDeleteImage("backImage")}
                    className="absolute cursor-pointer top-[10px] right-[10px]"
                  />
                </div>
              ) : (
                <div className="bg-[#EAECF0] py-5 w-full  relative text-center rounded-[20px] h-[250px]">
                  <div className="text-center">
                    <UploadIcon />
                  </div>
                  <h6 className="text-[14px] font-Mulish font-normal w-[90%] sm:w-[80%] mx-auto text-darkgrey mb-3 mt-4">
                    Medicare card
                  </h6>
                  <p className="text-[12px] font-normal text-[#98A2B3] w-[90%] sm:w-[80%] mx-auto mb-5">
                    Upload the Medicare card of the document supports: JPG,
                    PNG,PDF
                  </p>
                  <div className="m-auto">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,application/pdf"
                      {...register("backImage", {
                        required: !isValidPassport?.backImage
                          ? "File is required"
                          : false,
                      })}
                      // onChange={(e) => handleFileChange(e, "back")}
                      onChange={handleBackFileChange}
                      ref={backFileInputRef}
                      hidden
                    />
                    <button
                      type="button"
                      onClick={() => handleFileChooseClick("back")}
                      className="border border-solid border-[#ccc] text-sm font-normal  text-[#585C7B]  rounded-full px-4 py-2"
                    >
                      Choose file
                    </button>
                  </div>
                  {errors.backImage && (
                    <ErrorsMessage
                      title={errors.backImage.message}
                      className="absolute top-[100%] left-0"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-between my-10">
          <PreviousButtom onClick={() => dispatch(prevStep())}>
            Previous
          </PreviousButtom>
          <ContinueButton type="submit" loading={isPending}>
            Next
          </ContinueButton>
        </div>
      </form>
    </>
  );
};

export default UploadCertificateMedicareCard;
